'use client';

// Type definitions for Google Preferred Sources SDK
export interface GooglePreferredSourceConfig {
  theme?: 'light' | 'dark';
  lang?: string;
}

export interface GooglePreferredSourceApi {
  init: (config?: GooglePreferredSourceConfig) => void;
  addPreferredSource: () => void;
}

declare global {
  interface Window {
    PREFERRED_SOURCE?:
      | ((api: GooglePreferredSourceApi) => void)[]
      | {
          push: (...callbacks: ((api: GooglePreferredSourceApi) => void)[]) => void;
          ready: () => Promise<GooglePreferredSourceApi>;
          api?: GooglePreferredSourceApi;
        };
  }
}

/**
 * Programmatically triggers Google's "Add to Preferred Sources" prompt.
 * Uses the Advanced JS integration (Method 2) with manual control.
 */
export async function triggerGooglePreferredSource(
  config: GooglePreferredSourceConfig = { theme: 'light', lang: 'en' }
): Promise<void> {
  if (typeof window === 'undefined') return;

  // 1. If publisher.js is already initialized and api is ready
  const psGlobal = window.PREFERRED_SOURCE as any;
  if (psGlobal && psGlobal.api && typeof psGlobal.api.addPreferredSource === 'function') {
    try {
      psGlobal.api.init(config);
    } catch {
      // init may already be done
    }
    psGlobal.api.addPreferredSource();
    return;
  }

  // 2. If publisher.js is loaded with the ready() promise
  if (psGlobal && typeof psGlobal.ready === 'function') {
    try {
      const api = await psGlobal.ready();
      if (api && typeof api.addPreferredSource === 'function') {
        try {
          api.init(config);
        } catch {
          // ignore already initialized
        }
        api.addPreferredSource();
        return;
      }
    } catch (e) {
      console.warn('Error awaiting PREFERRED_SOURCE ready promise:', e);
    }
  }

  // 3. Fallback: Queue via standard PREFERRED_SOURCE callback queue
  if (Array.isArray(window.PREFERRED_SOURCE) || (psGlobal && typeof psGlobal.push === 'function')) {
    const queue = window.PREFERRED_SOURCE as any;
    queue.push((api: GooglePreferredSourceApi) => {
      try {
        api.init(config);
      } catch {
        // ignore
      }
      api.addPreferredSource();
    });
    return;
  }

  // 4. Backup Fallback: Open Google Preference URL directly if script failed/blocked
  const siteUrl = window.location.hostname || 'public.app';
  window.open(
    `https://www.google.com/preferences/source?q=${encodeURIComponent(siteUrl)}`,
    '_blank',
    'noopener,noreferrer'
  );
}
